import type { RoomsParticipantDistribution } from '@/domain/shared/types';
import { ADULTS_DEFAULT_AGE, BABIES_DEFAULT_AGE, CHILDREN_DEFAULT_AGE } from '@/domain/shared/constants';
import { sub } from 'date-fns';

interface Dependencies {
  todayDate: Date;
}

export function createRoomsParticipantDistributionMapper(dependencies: Partial<Dependencies> = {}) {
  const { todayDate = new Date() } = dependencies;

  const getAdultDate = () => {
    const thirtyYearsAgo = subtractYearsToDate(todayDate, ADULTS_DEFAULT_AGE);
    return formatDate(thirtyYearsAgo);
  };

  const subtractYearsToDate = (date: Date, yearsToSubtract: number) => {
    const currentYear = date.getFullYear();
    const newDate = new Date(date);

    newDate.setFullYear(currentYear - yearsToSubtract);

    return newDate;
  };

  const formatDate = (date: Date) => {
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() - offset * 60 * 1000).toISOString().split('T')[0];
  };

  return (roomsParticipantDistribution: RoomsParticipantDistribution) =>
    roomsParticipantDistribution.map(({ adults, children, babies }) => [
      ...Array.from({ length: adults }).map(getAdultDate),
      ...children.map(formatDate),
      ...babies.map(formatDate),
    ]);
}

export function generateRoomsParticipantDistributionWithDefaultBirthDates(
  adults: number,
  children: number,
  babies: number,
  todayDate: Date = new Date()
) {
  const childrenArray = Array.from({ length: children }, () =>
    sub(todayDate, { years: CHILDREN_DEFAULT_AGE, days: 1 })
  );
  const babiesArray = Array.from({ length: babies }, () => sub(todayDate, { years: BABIES_DEFAULT_AGE, days: 1 }));
  return { adults, children: childrenArray, babies: babiesArray };
}
